import {
  applyNodeChanges,
  type Node,
  type NodeChange,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import { useEffect, useMemo, useState } from 'react';
import { useLatest } from 'react-use';

import { useHelperLines } from '@/components/diagrams/FlowChart/components/HelperLines';
import { useAsyncLayout } from '@/components/diagrams/FlowChart/hooks/useAsyncLayout';
import { diagnostics } from '@/utils/diagnostics';

import { type DiagramBuilder_DiagramFragment } from '../graphql/Diagram.generated';
import {
  getLayoutKey,
  getTileSizes,
  hasSavedPositions,
  layoutDiagram,
} from '../utils/diagramLayout';
import { DIAGRAM_FIT_VIEW_OPTIONS } from '../utils/diagramLayout.config';
import { getStageBoundaries } from '../utils/diagramStages';
import {
  canUseLayoutRoute,
  createReactFlowDiagram,
  type ReactFlowDiagram,
  type ReactFlowDiagramNode,
} from '../utils/reactFlowDiagram';

/**
 * Holds the live React Flow diagram of the builder canvas: the tiles and
 * connections, the drag state, and the automatic layout.
 *
 * `diagram` is the saved document. The React Flow diagram comes from it, and
 * adds the state that is not saved: measured tile sizes, the selection, the
 * tile positions during a drag, and the ELK routes. This hook does not change
 * `diagram`. An edit changes `diagram`, and the React Flow diagram follows it.
 *
 * ELK lays out the diagram after React Flow measures every tile, as in the
 * estate waterfall. It runs again only when the layout key changes, or when
 * the user selects "Auto tiles". A text edit can change the size of a tile,
 * but it does not run ELK. A diagram whose tiles all have saved positions
 * does not need ELK.
 *
 * Routes are valid only for the positions that ELK solved. They do not show
 * while a tile moves, or when saved positions replace the ELK positions. A
 * connection with a set side, route shape, or waypoints never uses them.
 *
 * `stageBoundaries` gives the divider before each stage after the first.
 * A tile drag does not move the dividers or the other stages. The automatic
 * layout gives the divider heights while it places every tile.
 *
 * A dragged or resized tile snaps to the edges and centers of the other
 * tiles. `helperLines` gives the alignment lines to show while it moves.
 *
 * Each tile calls `onDelete` from its Delete button. The shared tile node
 * shows this button only when its node data has an `onDelete` function. The
 * other tile edits need no function in the node data: the tile text modal
 * gets `editNode` from the editing context, the selection bar is outside
 * React Flow, and React Flow reports drags and resizes through
 * `onNodesChange`. `DiagramFlowChart` adds the label actions of the
 * connections and stage bands, because it copies those nodes and edges. It
 * does not copy the tiles, so the tile action goes in here. Give a stable
 * function: a new function creates every tile again.
 */
export function useReactFlowDiagram(
  diagram: DiagramBuilder_DiagramFragment,
  onDelete?: (nodeId: string) => void
) {
  // The tiles and arrows of `diagram`, without the React Flow state.
  const fromDiagram = useMemo((): ReactFlowDiagram => {
    const created = createReactFlowDiagram(diagram);
    return {
      ...created,
      nodes: created.nodes.map((node) => ({
        ...node,
        data: { ...node.data, onDelete },
      })),
    };
  }, [diagram, onDelete]);
  const [nodes, setNodes] = useNodesState(fromDiagram.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(fromDiagram.edges);
  const [dragging, setDragging] = useState(false);
  const isPinned = diagram.content.nodes.every((node) => node.position);
  // The node state updates in an effect after the content changes. Take the
  // tile list and set widths from the content, and measured sizes from the state.
  const tileSizes = getTileSizes(fromDiagram, nodes);
  const latest = useLatest({ diagram, tileSizes });
  const layout = useAsyncLayout(
    isPinned || !tileSizes ? null : getLayoutKey(diagram, fromDiagram),
    // The input is null until every tile has a size, so the sizes exist here.
    () => layoutDiagram(latest.current.diagram, latest.current.tileSizes ?? {}),
    (error) => diagnostics.warn('diagram builder layout failed', { error })
  );
  const { result } = layout;
  const isPlaced = result !== null || isPinned || layout.didFail;

  // Keep the React Flow state of each tile, such as its size, selection, and
  // position, when the diagram content changes. Saved positions come first.
  useEffect(() => {
    setNodes((current) =>
      fromDiagram.nodes.map((node) => {
        const previous = current.find((item) => item.id === node.id);
        return {
          ...node,
          measured: previous?.measured,
          selected: previous?.selected,
          position:
            node.data.item.position ?? previous?.position ?? node.position,
        };
      })
    );
    setEdges((current) =>
      fromDiagram.edges.map((edge) => ({
        ...edge,
        selected: current.find((item) => item.id === edge.id)?.selected,
      }))
    );
  }, [fromDiagram, setNodes, setEdges]);

  // A new layout moves every tile that has no saved position.
  useEffect(() => {
    if (!result) return;
    const placed = new Map(result.nodes.map((node) => [node.id, node]));
    setNodes((current) =>
      current.map((node) => ({
        ...node,
        position: placed.get(node.id)?.position ?? node.position,
      }))
    );
  }, [result, setNodes]);

  const { fitView } = useReactFlow();
  // Fit the view after React Flow renders the first placed tiles. This is the
  // only automatic fit, so a tile that the user adds later stays where they
  // clicked.
  useEffect(() => {
    if (!isPlaced) return;
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        void fitView(DIAGRAM_FIT_VIEW_OPTIONS);
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [isPlaced, fitView]);

  const { helperLineHorizontal, helperLineVertical, getHelperLinesChanges } =
    useHelperLines();

  const onNodesChange = (changes: NodeChange<Node>[]) => {
    // The default helper line configuration uses only tiles, so the stage
    // dividers do not snap or show lines.
    //
    // React Flow also reports a `dimensions` change when it measures a node
    // whose content changed, such as a new display. The helper lines treat
    // each size change as a resize, so they get only the drags and resizes
    // of the user. An empty change list hides the lines.
    const isGesture = changes.some(
      (change) =>
        (change.type === 'position' && change.dragging) ||
        (change.type === 'dimensions' && change.resizing)
    );
    const gestureChanges = getHelperLinesChanges(
      isGesture ? changes : [],
      nodes
    );
    const snapped = isGesture ? gestureChanges : changes;
    // React Flow also reports changes to the stage dividers, which are not
    // tiles. The diagram content decides which tiles exist, so changes that
    // add or replace a node do not apply.
    const tileChanges = snapped.filter(
      (change): change is NodeChange<ReactFlowDiagramNode> =>
        change.type !== 'add' &&
        change.type !== 'replace' &&
        nodes.some((node) => node.id === change.id)
    );
    setNodes((current) => applyNodeChanges(tileChanges, current));
  };

  const layoutBoundaries = hasSavedPositions(diagram)
    ? null
    : result?.stageBoundaries;
  const stageBoundaries = useMemo(
    () => getStageBoundaries(diagram, layoutBoundaries),
    [diagram, layoutBoundaries]
  );

  const showRoutes =
    !dragging && !layout.isPending && !layout.didFail && !isPinned;
  // Keep the identity of the edge list until the edges or their routes
  // change, so that the canvas does not render every edge again.
  const routedEdges = useMemo(() => {
    const routes = new Map(result?.edges.map((edge) => [edge.id, edge.data]));
    return edges.map((edge) => {
      const route =
        showRoutes && canUseLayoutRoute(edge) ? routes.get(edge.id) : undefined;
      return {
        ...edge,
        data: {
          ...edge.data!,
          routeWaypoints: route?.routeWaypoints,
          labelPosition: route?.labelPosition,
        },
      };
    });
  }, [edges, result, showRoutes]);

  return {
    nodes,
    edges: routedEdges,
    onNodesChange,
    onEdgesChange,
    /** The alignment lines to show while the user drags or resizes a tile. */
    helperLines: {
      horizontal: helperLineHorizontal,
      vertical: helperLineVertical,
    },
    /** The divider before each stage after the first, at its canvas height. */
    stageBoundaries,
    /** False until the tiles have positions to show. */
    isPlaced,
    isArranging: !layout.didFail && (layout.isPending || !isPlaced),
    didFail: layout.didFail,
    dragging,
    setDragging,
    retryLayout: layout.retry,
  };
}
